import { useOrganizations } from '@hotjar/organization';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { OVERVIEW, SURVEYS } from '@hotjar/webapp/constants/routes';
import { ONBOARDING } from '@hotjar/webapp/constants/routes/onboarding';
import { NO_SITE_ACCESS } from '@hotjar/webapp/constants/routes/status';
import { getLoggedIn } from '@hotjar/webapp/domains/common/user-data';
import { useTranslationKeyForWindowTitle } from '@hotjar/webapp/hooks/useTranslatedTitle';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Registration } from './Registration';

export function RegistrationPage(): JSX.Element | null {
  const isLoggedIn = getLoggedIn();
  const { data: currentUser } = useCurrentUser();
  const { data: organizations } = useOrganizations(currentUser?.id);
  const { data: currentSite, isLoading: isCurrentSiteLoading } = useCurrentSite();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useTranslationKeyForWindowTitle('common:registration.title');

  useEffect(() => {
    // This useEffect handles the case where the user is logged in and we
    // need to decide where to navigate them to rather than the registration page.
    if (!isLoggedIn) {
      return;
    }

    // If sites list is empty useCurrentSite will return `undefined`,
    // redirect to no-site-access. This happens when the user is part
    // of the organization but hasn't been added to any site yet.
    if (!currentSite && !isCurrentSiteLoading) {
      navigate(NO_SITE_ACCESS.path);

      return;
    }

    if (currentUser && organizations && currentSite) {
      if (currentUser.isDemoUser) {
        return;
      }

      // User can be now authenticated when still on registration form.
      // This will happen after the person has created the user entity
      // after successfully submitting the password step. If they refresh
      // the page they should be redirected to onboarding.
      if (organizations.length === 0) {
        navigate(getRoute(ONBOARDING.path));

        return;
      }

      // If search Params has  a survey_template_id, redirect to survey create page
      const surveyTemplateId = searchParams.get('survey_template_id');
      if (surveyTemplateId) {
        navigate(
          getRoute(SURVEYS.CREATE.path, {
            templateId: surveyTemplateId,
            activeFlowSection: 'name',
            siteId: currentSite.id,
          }),
        );

        return;
      }

      const overviewRoute = getRoute(OVERVIEW.BASE.path, {
        siteId: currentSite?.id,
      });

      navigate(`${overviewRoute}${window.location.search}`);
    }
  }, [
    currentSite,
    currentUser,
    isCurrentSiteLoading,
    isLoggedIn,
    navigate,
    organizations,
    searchParams,
  ]);

  if (isLoggedIn && currentUser?.isDemoUser === false) {
    return null;
  }

  return <Registration />;
}
